import { DOCUMENT } from "@angular/common";
import { HttpClient, HttpErrorResponse, HttpResponse } from "@angular/common/http";
import { Inject, Injectable } from "@angular/core";
import JwtDecode from "jwt-decode";
import { Observable } from "rxjs";
import { map } from "rxjs/operators";
import { APIS } from "src/app/apis";
import { NAVIGATION_ROUTES } from "src/app/navigation-routes.const";
import { IMoovTokenSuccessResponse, MoovTokenErrorCode, MoovTokenErrorResponse } from "../../interfaces";
import { WindowRef } from "../../modules/window-reference";
import HttpStatusCode from "../../modules/window-reference/http-status-code.enum";
import { AbstractLocalStorageService } from "../localStorage/local-storage.abstract.service";
import { LocalStorageKeys } from "../localStorage/local-storage.enum";
import { PersonalUserData, PersonalUserDataWithToken } from "../personal-user-data/personal-user-data";
import { AbstractAuthorizationService } from "./authorization.abstract.service";

@Injectable({
    providedIn: "root",
})
export class AuthorizationService implements AbstractAuthorizationService {
    private updateLoggedInUserTokenUrl = `${APIS.auth}/updateLoggedInUserToken`;
    private tokenLoginUrl = `${APIS.auth}/tokenLogin`;
    private registerUserFromTokenUrl = `${APIS.auth}/registerUserFromToken`;
    private validateTokenlessRegistrationForCustomerUrl = `${APIS.auth}/validateTokenlessRegistrationForCustomer`;

    constructor(
        @Inject(DOCUMENT) private document: Document,
        private windowRef: WindowRef,
        private http: HttpClient,
        private localStorageService: AbstractLocalStorageService,
    ) {}

    resetMoovToken(): void {
        this.localStorageService.clear(LocalStorageKeys.moovToken);
    }

    getMoovToken(userMail: string, password: string): Observable<HttpResponse<IMoovTokenSuccessResponse>> {
        const token = this.http.post<IMoovTokenSuccessResponse>(
            this.getTokenUrl(),
            { userMail: userMail, password: password },
            { observe: "response" },
        );

        return token;
    }

    updateLoggedInUserToken(): Observable<void> {
        return this.http.post<IMoovTokenSuccessResponse>(this.updateLoggedInUserTokenUrl, {}, {}).pipe(
            map((response: IMoovTokenSuccessResponse) => {
                const token = response.token;
                this.localStorageService.write<string>(LocalStorageKeys.moovToken, token);
                this.saveUserDetailsLocally(token);
            }),
        );
    }

    saveUserDetailsLocally(token: string): void {
        const userInfo = JwtDecode<PersonalUserDataWithToken>(token);

        this.localStorageService.write<PersonalUserData>(LocalStorageKeys.userPersonalInfo, userInfo);
    }

    getTokenUrl(): string {
        return APIS.login;
    }

    getValidateTokenlessRegistrationForCustomerUrl(): string {
        return this.validateTokenlessRegistrationForCustomerUrl;
    }

    getTokenLoginUrl(): string {
        return this.tokenLoginUrl;
    }

    getRegisterUserFromTokenUrl(): string {
        return this.registerUserFromTokenUrl;
    }

    isTokenExpired(token: string): boolean {
        const userInfo = JwtDecode<PersonalUserDataWithToken>(token);
        const expiredInUnixTimestamp = userInfo.exp;
        const expiredDate = new Date(0);
        expiredDate.setUTCSeconds(expiredInUnixTimestamp);
        return this.isExpireDatePass(expiredDate.getTime());
    }

    isExpireDatePass(expiredDate: number): boolean {
        return new Date().getTime() > expiredDate;
    }

    isSessionExpiredRequestError(err: MoovTokenErrorResponse): boolean {
        return (
            this.isUnauthorizedError(err) &&
            (err.error?.errorCode === MoovTokenErrorCode.MOOV_SESSION_EXPIRED ||
                err.error?.errorCode === MoovTokenErrorCode.AUTHENTICATION_ERROR)
        );
    }

    isUnauthorizedError(err: MoovTokenErrorResponse): boolean {
        return err.status === HttpStatusCode.UNAUTHORIZED;
    }

    isMoovTokenSessionExpiredRequestError(err: HttpErrorResponse): boolean {
        return (
            err instanceof MoovTokenErrorResponse &&
            this.isUnauthorizedError(err) &&
            err.error?.errorCode === MoovTokenErrorCode.MOOV_SESSION_EXPIRED
        );
    }

    getReturnUrl(removeReturnUrl: boolean): string {
        const existReturnUrl = this.localStorageService.get<string>(LocalStorageKeys.returnUrl);
        if (existReturnUrl && removeReturnUrl) {
            this.localStorageService.clear(LocalStorageKeys.returnUrl);
        }
        const currentUrl = this.document.location.pathname.slice(1);
        const returnUrl: string =
            currentUrl === NAVIGATION_ROUTES.sessionExpired
                ? existReturnUrl || NAVIGATION_ROUTES.home
                : this.document.location.href;
        return this.windowRef.nativeWindow.encodeURIComponent(returnUrl);
    }

    tokenLogin(tokenBody: string): Observable<boolean> {
        console.log(`tokenLoging calling ${this.tokenLoginUrl} with tokenBody ${tokenBody}`);
        let result = this.http.post<boolean>(this.tokenLoginUrl, { tokenBody: tokenBody });

        return result;
    }

    registerUserFromToken(
        tokenBody: string,
        customerId: string,
        firstName: string,
        lastName: string,
        mailAddress: string,
        userInputOrg: string,
        userInputRole: string,
        locale: string,
        gender: number,
        inMgmtPos: boolean,
        isApproveShareMotivations: boolean,
        password: string,
    ): Observable<HttpResponse<IMoovTokenSuccessResponse>> {
        return this.http.post<IMoovTokenSuccessResponse>(
            this.registerUserFromTokenUrl,
            {
                tokenBody: tokenBody,
                customerId: customerId,
                firstName: firstName,
                lastName: lastName,
                mailAddress: mailAddress,
                userInputOrg: userInputOrg,
                userInputRole: userInputRole,
                locale: locale,
                gender: gender,
                inMgmtPos: inMgmtPos,
                approveShareMotivations: isApproveShareMotivations,
                password: password,
            },
            { observe: "response" },
        );
    }

    validateTokenlessRegistrationForCustomer(customerId: string): Observable<boolean> {
        return this.http.get<boolean>(`${this.validateTokenlessRegistrationForCustomerUrl}?customerId=${customerId}`);
    }
}
